import { motion } from "framer-motion";
import { Truck, LayoutGrid, ChefHat, Store } from "lucide-react";
import foodTruckBg from "@/assets/landing/food-truck-bg.png";
import foodHallBg from "@/assets/landing/food-hall-bg.png";
import ghostKitchenBg from "@/assets/landing/ghost-kitchen-bg.png";
import brickMortarBg from "@/assets/landing/brick-mortar-bg.png";
import type { LucideIcon } from "lucide-react";

interface BusinessType {
  title: string;
  icon: LucideIcon;
  image: string;
}

const businessTypes: BusinessType[] = [
  {
    title: "Food Trucks",
    icon: Truck,
    image: foodTruckBg,
  },
  {
    title: "Food Hall Concepts",
    icon: LayoutGrid,
    image: foodHallBg,
  },
  {
    title: "Ghost Kitchens",
    icon: ChefHat,
    image: ghostKitchenBg,
  },
  {
    title: "Brick & Mortar",
    icon: Store,
    image: brickMortarBg,
  },
];

export const WhoItsForSection = () => {
  return (
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-midnight tracking-tight">
            Built for{" "}
            <span className="text-vault-green">
              small, owner-operated food businesses
            </span>
          </h2>
          <p className="text-lg text-slate-brand max-w-3xl mx-auto">
            Dollar Bill is made for:
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-7xl mx-auto">
          {businessTypes.map((business, index) => {
            const Icon = business.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="group relative aspect-[3/4] rounded-2xl overflow-hidden cursor-default"
              >
                <img
                  src={business.image}
                  alt={business.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 transition-all duration-300 group-hover:from-black/85 group-hover:via-black/40" />

                <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center mb-3 transition-colors duration-300 group-hover:bg-vault-green/80 group-hover:border-vault-green/60">
                    <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-white leading-tight">
                    {business.title}
                  </h3>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-base text-slate-brand max-w-3xl mx-auto text-center leading-relaxed mt-12"
        >
          We focus on food businesses doing up to $700K in annual sales.
          Dollar Bill eliminates the stressful financial tasks that slow you
          down — so you can spend more time{" "}
          <span className="font-semibold text-midnight">
            running your business
          </span>
          ,{" "}
          <span className="font-semibold text-midnight">
            being with your family
          </span>
          , and{" "}
          <span className="font-semibold text-midnight">
            planning what's next
          </span>
          .
        </motion.p>
      </div>
    </section>
  );
};
